import { defineStore } from 'pinia'
import moment from 'moment'
import numeral from 'numeral'
import {
    getSalesChart,
    getSalesDetail,
    getSalesSummary,
} from '@/services/dashboardSalesChart.service'
import { isApiSuccess, unwrapApiData } from '@/services/apiEnvelope'

const defaultSummary = () => ({
    profit_total_today: '$0.00',
    profit_total_yesterday: '$0.00',
    quote_total_today: '$0.00',
    quote_total_yesterday: '$0.00',
    sales_total_today: '$0.00',
    sales_total_yesterday: '$0.00',
    profit_total_month: '$0.00',
    profit_total_month_last: '$0.00',
    quote_total_month: '$0.00',
    quote_total_month_last: '$0.00',
    sales_total_month: '$0.00',
    sales_total_month_last: '$0.00',
    profit_total_year: '$0.00',
    profit_total_year_last: '$0.00',
    quote_total_year: '$0.00',
    quote_total_year_last: '$0.00',
    sales_total_year: '$0.00',
    sales_total_year_last: '$0.00',
    diff_profit_day: '$0.00',
    diff_quotes_day: '$0.00',
    diff_sales_day: '$0.00',
    diff_profit_month: '$0.00',
    diff_quotes_month: '$0.00',
    diff_sales_month: '$0.00',
    diff_profit_year: '$0.00',
    diff_quotes_year: '$0.00',
    diff_sales_year: '$0.00',
})

const safeNumber = (value) => {
    const num = Number(value)
    return Number.isFinite(num) ? num : 0
}

const formatCurrency = (value) => numeral(safeNumber(value)).format('$0,0,0,0,0')
const formatPercent = (value) => numeral(safeNumber(value)).format('0,0,0,0,0%')

const buildSummary = (dashboard = {}) => {
    const summary = defaultSummary()

    const currencyFields = [
        'profit_total_today',
        'profit_total_yesterday',
        'quote_total_today',
        'quote_total_yesterday',
        'sales_total_today',
        'sales_total_yesterday',
        'profit_total_month',
        'profit_total_month_last',
        'quote_total_month',
        'quote_total_month_last',
        'sales_total_month',
        'sales_total_month_last',
        'profit_total_year',
        'profit_total_year_last',
        'quote_total_year',
        'quote_total_year_last',
        'sales_total_year',
        'sales_total_year_last',
    ]

    currencyFields.forEach((field) => {
        summary[field] = formatCurrency(dashboard[field])
    })

    const diffMap = [
        ['diff_profit_day', 'profit_total_today', 'profit_total_yesterday'],
        ['diff_quotes_day', 'quote_total_today', 'quote_total_yesterday'],
        ['diff_sales_day', 'sales_total_today', 'sales_total_yesterday'],
        ['diff_profit_month', 'profit_total_month', 'profit_total_month_last'],
        ['diff_quotes_month', 'quote_total_month', 'quote_total_month_last'],
        ['diff_sales_month', 'sales_total_month', 'sales_total_month_last'],
        ['diff_profit_year', 'profit_total_year', 'profit_total_year_last'],
        ['diff_quotes_year', 'quote_total_year', 'quote_total_year_last'],
        ['diff_sales_year', 'sales_total_year', 'sales_total_year_last'],
    ]

    diffMap.forEach(([diffField, currentField, previousField]) => {
        const currentValue = safeNumber(dashboard[currentField])
        const previousValue = safeNumber(dashboard[previousField])
        const diff = previousValue === 0 ? 0 : (currentValue - previousValue) / previousValue
        summary[diffField] = formatPercent(diff)
    })

    return summary
}

export const useDashboardSalesChartStore = defineStore('dashboardSalesChart', {
    state: () => ({
        summary: defaultSummary(),
        plotDataRaw: [],
        queryDataRaw: [],
        loading: false,
        error: null,
    }),
    actions: {
        resetDashboardSalesChart() {
            this.summary = defaultSummary()
            this.plotDataRaw = []
            this.queryDataRaw = []
            this.loading = false
            this.error = null
        },
        setPlotDataRaw(data = []) {
            this.plotDataRaw = data || []
        },
        setQueryDataRaw(data = []) {
            this.queryDataRaw = data || []
        },
        async fetchSalesSummary({ customer = '', user = '', to_date = true } = {}) {
            this.loading = true
            this.error = null

            try {
                const response = await getSalesSummary({
                    local_datetime: moment().toISOString(true),
                    customer,
                    user,
                    to_date,
                })

                const data = unwrapApiData(response, {})
                if (isApiSuccess(response) && data) {
                    this.summary = buildSummary(data)
                } else {
                    this.summary = defaultSummary()
                }

                return response.data
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to fetch sales summary', error)
                throw error
            } finally {
                this.loading = false
            }
        },
        async fetchSalesChart({ begin_date, end_date, period } = {}) {
            this.loading = true
            this.error = null
            this.setPlotDataRaw([])

            try {
                const response = await getSalesChart({
                    begin_date,
                    end_date,
                    period,
                })

                const data = unwrapApiData(response, [])
                if (isApiSuccess(response)) {
                    this.setPlotDataRaw(data || [])
                } else {
                    this.setPlotDataRaw([])
                }

                return response.data
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to fetch sales chart', error)
                throw error
            } finally {
                this.loading = false
            }
        },
        async fetchSalesDetail({ begin_date, end_date } = {}) {
            this.loading = true
            this.error = null
            this.setQueryDataRaw([])

            try {
                const response = await getSalesDetail({
                    begin_date,
                    end_date,
                })

                const data = unwrapApiData(response, {})
                if (isApiSuccess(response)) {
                    this.setQueryDataRaw(data?.sales || [])
                } else {
                    this.setQueryDataRaw([])
                }

                return response.data
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to fetch sales detail', error)
                throw error
            } finally {
                this.loading = false
            }
        },
    },
})
